import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { LineMask, FadeIn, SectionTag } from "../components/marketing/Reveal";

const STAGES = [
  { t: "Create your professional account", d: "Register with your clinic details. Your account gives you a private, secure portal for every case — submissions, files, messages and history in one place." },
  { t: "Submit a case", d: "Provide the patient/case reference, arch, case type, software preference, treatment goals and clinical notes. Upload intraoral scans (STL/OBJ/PLY), photographs, X-rays and supporting documents." },
  { t: "Receive your Case ID", d: "Every submission gets a unique Case ID (e.g. OPX-7K2Q4M). You receive an email confirmation and can track the case live from your portal." },
  { t: "Files under review", d: "Our team verifies records completeness and quality. If anything is missing, we message you directly inside the case thread." },
  { t: "Planning in progress", d: "Clinical assessment, digital strategy, staging and biomechanics — attachments, IPR, anchorage and occlusion built around your stated objectives." },
  { t: "Doctor review", d: "The proposed plan lands in your portal. Review it on your schedule, discuss with the planning team, and request revisions with structured notes." },
  { t: "Final plan delivered", d: "Approve the plan, download the final files and move to production. The complete case record stays in your history for reference." },
];

const FAQS_SHORT = ["Secure file handling & access control", "Email alerts at every status change", "Typical 48-hour turnaround"];

export default function HowItWorks() {
  return (
    <MarketingLayout>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <section className="pt-40 pb-16 lg:pb-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <SectionTag>How It Works</SectionTag>
            <h1 className="font-display font-black tracking-tight leading-[0.98] text-[clamp(2.6rem,6.5vw,5.5rem)] max-w-4xl">
              <LineMask delay={0.1}>Seven steps.</LineMask>
              <LineMask delay={0.25} className="text-neon">Zero friction.</LineMask>
            </h1>
            <FadeIn delay={0.5}>
              <div className="mt-8 flex flex-wrap gap-3">
                {FAQS_SHORT.map((f) => (
                  <span key={f} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-medium text-slate-300">
                    <CheckCircle2 className="h-3.5 w-3.5 text-neon" /> {f}
                  </span>
                ))}
              </div>
            </FadeIn>
          </div>
        </section>

        <section className="pb-24 lg:pb-32">
          <div className="max-w-4xl mx-auto px-6 lg:px-10">
            {STAGES.map((s, i) => (
              <FadeIn key={s.t} delay={0.03}>
                <div className="relative grid grid-cols-[auto_1fr] gap-6 lg:gap-10 pb-14 last:pb-0">
                  {i < STAGES.length - 1 && <span className="absolute left-[27px] top-14 bottom-0 w-px bg-white/10" aria-hidden="true" />}
                  <div className="relative z-10 h-14 w-14 rounded-full bg-ink-2 border border-neon/40 flex items-center justify-center font-display font-black text-neon text-lg shrink-0">
                    {i + 1}
                  </div>
                  <div className="pt-2">
                    <h2 className="font-display font-bold text-xl lg:text-2xl tracking-tight">{s.t}</h2>
                    <p className="mt-3 text-sm md:text-base text-slate-400 leading-relaxed max-w-2xl">{s.d}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn>
            <div className="mt-20 text-center">
              <Link to="/register" data-testid="hiw-cta-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(0,229,255,0.45)]">
                Submit a Case now <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </FadeIn>
        </section>
      </motion.div>
    </MarketingLayout>
  );
}
